import React, { useState, useEffect } from "react";
import CustomSelect from "@/components/CustomSelect";
import {
  faLocationDot,
  faChevronDown,
  faCheck
} from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import CarouselFlex from "@/components/CarouselFlex";
import {
  chips,
  filterDataInitial,
  getCitiesOptions,
  getNeighborhoodsOptions,
  getStatesOptions
} from "@/utils/tsx/filters";
import styles from "./styles.module.scss";
import type {
  FilterDataProps,
  CityItem,
  NeighborhoodItem,
  StoreListItem
} from "@/types/filters";
import classNames from "classnames";
import MaskedTextField from "../MaskedTextField";
import { useWindowSize } from "@/hooks/useWindowSize";
import { validateZipcode } from "@/utils/helpers";

export interface FiltersProps {
  filterData: FilterDataProps;
  // eslint-disable-next-line no-unused-vars
  onChange: (data: FilterDataProps) => void;
  onFindLocation: () => void;
  // eslint-disable-next-line no-unused-vars
  onFilter: (data: FilterDataProps) => void;
  storeListData: StoreListItem[];
}

export const Filters = ({
  filterData,
  onChange,
  onFilter,
  onFindLocation,
  storeListData
}: FiltersProps) => {
  const { state, city, neighborhood, zipcode, filterBy } = filterData;

  const { mobile } = useWindowSize();

  const statesOptions = getStatesOptions(storeListData);

  const [cityOptions, setCityOptions] = useState<CityItem[]>([]);
  const [neighborhoodOptions, setNeighborhoodOptions] = useState<
    NeighborhoodItem[]
  >([]);
  const [zipcodeError, setZipcodeError] = useState(false);

  useEffect(() => {
    const filteredCities = state ? getCitiesOptions(storeListData, state) : [];
    setCityOptions(filteredCities);
    setNeighborhoodOptions([]);
    onChange({ ...filterData, city: "", neighborhood: "" });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state, storeListData]);

  useEffect(() => {
    if (state && city) {
      const filteredNeighborhoods = getNeighborhoodsOptions(
        storeListData,
        state,
        city
      );
      setNeighborhoodOptions(filteredNeighborhoods);
      onChange({ ...filterData, neighborhood: "" });
    } else {
      setNeighborhoodOptions([]);
      onChange({ ...filterData, neighborhood: "" });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [city, storeListData]);

  const handleChange = (id: string, value: string) => {
    if (id === "filterBy") {
      return onChange({
        ...filterDataInitial,
        [id]: value as "zipcode" | "address"
      });
    }

    onChange({ ...filterData, [id]: value });
  };

  const handleChangeChip = (title: string) => {
    let newFilterData = { ...filterData };

    if (filterData.facilidades.includes(title)) {
      newFilterData = {
        ...filterData,
        facilidades: filterData.facilidades.filter(
          (facilidade) => facilidade !== title
        )
      };
    } else {
      newFilterData = {
        ...filterData,
        facilidades: [...filterData.facilidades, title]
      };
    }
    onChange(newFilterData);
  };

  const disabledFilter =
    (filterData.filterBy === "zipcode" &&
      !validateZipcode(filterData.zipcode)) ||
    (filterData.filterBy !== "zipcode" &&
      !filterData.city &&
      !filterData.state &&
      !filterData.neighborhood &&
      (storeListData.length === 0 ||
        (storeListData.length > 1 && filterData.facilidades.length === 0)));

  const handleKeyDown = (e: any) => {
    if (zipcodeError) {
      setZipcodeError(false);
    }

    if (e.key === "Enter" && filterData.filterBy === "zipcode") {
      const zipcodeIsValid = validateZipcode(filterData.zipcode);
      setZipcodeError(!zipcodeIsValid);

      if (zipcodeIsValid) {
        onFilter(filterData);
      }
    }
  };

  return (
    <div className={styles.filters}>
      <div className={`${styles["filters-panel"]}`}>
        <MaskedTextField
          type="text"
          value={zipcode}
          placeholder="Digite o CEP"
          onChange={(e) => handleChange("zipcode", e.target.value)}
          format="#####-###"
          fullWidth
          customSize="sm"
          onKeyDown={handleKeyDown}
          error={zipcodeError}
        />

        <button
          className={`${styles["btn-search"]} btn btn-md outlined`}
          onClick={() => {
            onFilter({ ...filterData, filterBy: "zipcode" });
          }}
          disabled={!validateZipcode(zipcode)}
        >
          Buscar
        </button>
      </div>
      <div className={`${styles["filters-input"]}`}>
        <button
          onClick={onFindLocation}
          className={`${styles["btn-local"]} ${styles.btnAcessibility} btn btn-md btnArea`}
        >
          <FontAwesomeIcon
            icon={faLocationDot}
            className={styles.iconLocation}
          />{" "}
          Usar localização atual
        </button>
        <div className={`${styles["line-flex"]}`}>
          <hr className="divider" /> <span>ou</span> <hr className="divider" />
        </div>
        <div
          className={classNames(
            styles.fieldsArea,
            filterBy === "zipcode" ? "form-zipcode" : "form-address"
          )}
        >
          <CustomSelect
            options={[
              { value: "zipcode", label: "CEP" },
              { value: "address", label: "Estado/Cidade" }
            ]}
            placeholder="Filtrar por"
            value={filterBy}
            onChange={(e: any) => handleChange("filterBy", e.target.value)}
            icon={faChevronDown}
            className="select-filter-by"
          />

          <div className={classNames(styles.rowFlex, `group-form-address`)}>
            <CustomSelect
              options={statesOptions}
              placeholder="Estado"
              value={state}
              onChange={(e: any) => handleChange("state", e.target.value)}
              icon={faChevronDown}
            />
            <CustomSelect
              options={cityOptions}
              placeholder="Cidade"
              value={city}
              onChange={(e): any => handleChange("city", e.target.value)}
              icon={faChevronDown}
              disabled={!state}
            />
            <CustomSelect
              options={neighborhoodOptions}
              placeholder="Bairro"
              value={neighborhood}
              onChange={(e: any) =>
                handleChange("neighborhood", e.target.value)
              }
              icon={faChevronDown}
              disabled={!city}
            />
          </div>
          <div className={classNames(styles.rowFlex, `group-form-zipcode`)}>
            <MaskedTextField
              type="text"
              value={zipcode}
              placeholder="Digite o CEP"
              onChange={(e) => handleChange("zipcode", e.target.value)}
              format="#####-###"
              fullWidth
              customSize="sm"
              onKeyDown={handleKeyDown}
              error={zipcodeError}
            />
          </div>
          <button
            onClick={() => {
              onFilter({
                ...filterData,
                filterBy: mobile ? "address" : filterBy,
                zipcode: mobile ? "" : zipcode
              });
            }}
            className={`${styles["searchButton"]} btn btn-md outlined`}
            disabled={disabledFilter}
          >
            Buscar
          </button>
        </div>
        <div className={`${styles.filterContainer}`}>
          <span className={styles.title}>Filtrar por:</span>
          <div className={styles.filterCarousel}>
            <CarouselFlex showArrows classNameArrow="arrow">
              {chips.map((chip, index) => {
                return (
                  <div className={styles.chip} key={index}>
                    <button
                      disabled={storeListData.length === 0}
                      style={{ borderWidth: "0.1rem" }}
                      onClick={() => handleChangeChip(chip.title)}
                      className={`btn chip-filter chip-lemon outline ${filterData.facilidades.includes(chip.title) ? "selected" : ""}`}
                    >
                      <span className={styles["chip-filter-icon"]}>
                        {chip.icon}
                      </span>
                      <span className="chip-filter-label">{chip.title}</span>

                      {filterData.facilidades.includes(chip.title) && (
                        <div className="chip-icon">
                          <FontAwesomeIcon
                            icon={faCheck}
                            className={styles["chip-check-icon"]}
                          />
                        </div>
                      )}
                    </button>
                  </div>
                );
              })}
            </CarouselFlex>
          </div>
        </div>
      </div>
    </div>
  );
};
